/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Display helpers for inbox rows and the reading pane. */

import colors from 'styles/tokens/colors';

/** `"Jane Doe" <jane@x.com>` -> { name: 'Jane Doe', email: 'jane@x.com' }. */
export function parseAddress(value, fallbackName = '') {
  const raw = String(value || '').trim();
  const match = raw.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (match) {
    const email = match[2].trim();
    return { name: match[1].trim() || fallbackName || email, email };
  }
  return { name: fallbackName || raw, email: raw };
}

/**
 * Split a header-style address list on commas or semicolons, ignoring
 * separators inside quotes or angle brackets, so `"Doe, Jane" <jane@x.com>`
 * stays one entry.
 */
export function splitAddressList(value) {
  const text = String(value || '');
  const parts = [];
  let current = '';
  let inQuotes = false;
  let inAngle = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '\\' && inQuotes && i + 1 < text.length) {
      current += ch + text[i + 1];
      i += 1;
    } else if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if (ch === '<' && !inQuotes) {
      inAngle = true;
      current += ch;
    } else if (ch === '>' && !inQuotes) {
      inAngle = false;
      current += ch;
    } else if ((ch === ',' || ch === ';') && !inQuotes && !inAngle) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
}

/** Bare addresses from a header-style list, de-duplicated case-insensitively. */
export function bareAddresses(value) {
  const seen = new Set();
  const out = [];
  (Array.isArray(value) ? value : splitAddressList(value)).forEach((entry) => {
    const { email } = parseAddress(entry);
    const address = String(email || '').trim();
    const key = address.toLowerCase();
    if (address && !seen.has(key)) {
      seen.add(key);
      out.push(address);
    }
  });
  return out;
}

/**
 * Reply-all recipients as bare addresses. From the inbox: the sender on To,
 * everyone else on To/Cc/Bcc on Cc. From Sent: the first original recipient on
 * To, the rest on Cc. This mailbox's own addresses are always left out.
 */
export function replyAllRecipients(email, { self = [], fromSent = false } = {}) {
  const own = new Set(bareAddresses(self.filter(Boolean)).map((addr) => addr.toLowerCase()));
  const original = bareAddresses(email?.to);
  const sender = bareAddresses(email?.from);
  const toList = fromSent ? original.slice(0, 1) : sender.slice(0, 1);
  const rest = fromSent ? original.slice(1) : original;
  const taken = new Set([...own, ...toList.map((addr) => addr.toLowerCase())]);
  const cc = bareAddresses([...rest, ...bareAddresses(email?.cc), ...bareAddresses(email?.bcc)]).filter(
    (addr) => !taken.has(addr.toLowerCase())
  );
  return { to: toList, cc };
}

export function initialsFor(name) {
  const cleaned = String(name || '').replace(/@.*$/, '').replace(/["'()<>[\]]/g, '').trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_TONES = [
  colors.status.info,
  colors.status.success,
  colors.status.warning,
  colors.status.neutral,
  colors.status.error,
];

/** A stable avatar colour pair for a sender. */
export function avatarTone(seed) {
  const text = String(seed || '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** List-row date: time today, "Yesterday", day and month this year, else a short date. */
export function formatListDate(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return '';
  if (sameDay(date, now)) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Reading-pane date, e.g. "Mon, 6 Oct 2026, 9:41 am". */
export function formatFullDate(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** Plain-text preview for a row when the provider sent no snippet. */
export function previewText(email) {
  if (email?.snippet) return email.snippet;
  const body = String(email?.body || '');
  return body.replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
}
